import { v4 as uuidv4 } from 'uuid';
import Utils from './utils';
import localStorageUtil from './localStorageUtil';

/**
 * S3PathUtil - Centralized S3 path prefix management
 */

const S3_PREFIX = 'public';

const buildPath = (path) => {
    if (!path) return '';
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `${S3_PREFIX}/${cleanPath}`;
};

// ============================================================================
// TRANSLATION PATHS
// ============================================================================

const getTranslationPath = (orgId, jobId) => {
    if (!orgId || !jobId) return '';
    return buildPath(`${orgId}/translations/${jobId}`);
};

// ============================================================================
// TICKET PATHS
// ============================================================================

const getTicketPath = (orgId, ticketId) => {
    if (!orgId || !ticketId) return '';
    return buildPath(`${orgId}/ticket/${ticketId}`);
};

const getTicketVectorDataPath = (orgId, ticketId) => {
    if (!orgId || !ticketId) return '';
    return `${getTicketPath(orgId, ticketId)}/vectordata-${ticketId}.json`;
};

const getTicketChatThreadPath = (orgId, ticketId, chatThreadId, userId, seq) => {
    if (!orgId || !ticketId || !chatThreadId || !userId || !seq) return '';
    return `${getTicketPath(orgId, ticketId)}/chatThread/${chatThreadId}/${userId}/${seq}`;
};

// ============================================================================
// TELEPHONE/VOICE PATHS
// ============================================================================

const getTelephoneTranscriptAudioPath = (orgId, ticketId) => {
    if (!orgId || !ticketId) return '';
    return `${getTicketPath(orgId, ticketId)}/transcript_audio_${ticketId}.mp3`;
};

const defineTelephoneTranscriptPath = (ticketId) => {
    if (!ticketId) return '';
    return `/ticket/${ticketId}/transcript_log`;
};

const defineTelephoneRecordingAudioPath = (ticketDetail) => {
    const { id: ticketId, affiliationOrganization } = ticketDetail;
    if (!ticketId || !affiliationOrganization) return '';

    return `public/${affiliationOrganization}/ticket/${ticketId}/transcript_audio_${ticketId}.mp3`;
};

const getPublicTelephoneRecordingAudioUrl = (ticketId) => {
    if (!ticketId) return '';

    const SDESK_DOMAIN = window.location.origin + '/';
    const organizationDomain = localStorageUtil.getCorporationDomain();
    return `${SDESK_DOMAIN}${organizationDomain}/ticket/${ticketId}/call_recording_audio`;
};

const getPublicTelephoneTranscriptUrl = (transcriptPath = '', ticketId = null) => {
    const SDESK_DOMAIN = window.location.origin + '/';
    const organizationDomain = localStorageUtil.getCorporationDomain();
    
    if (!Utils.isEmptyString(transcriptPath)) return `${SDESK_DOMAIN}${organizationDomain}${transcriptPath}`;

    if (!ticketId) return '';
    
    return `${SDESK_DOMAIN}${organizationDomain}${defineTelephoneTranscriptPath(ticketId)}`;
};

// ============================================================================
// CACHE PATHS
// ============================================================================

const getCachePath = (orgId) => {
    if (!orgId) return '';
    return buildPath(`tmp/cache/${orgId}`);
};

const getSystemFieldCachePath = (orgId) => {
    if (!orgId) return '';
    return `${getCachePath(orgId)}/system_field.json`;
};

const getSystemTagCachePath = (orgId) => {
    if (!orgId) return '';
    return `${getCachePath(orgId)}/system_tag.json`;
};

const getStatusItemsCachePath = (orgId, parentFieldId) => {
    if (!orgId || !parentFieldId) return '';
    return `${getCachePath(orgId)}/status_items_${parentFieldId}.json`;
};

// ============================================================================
// UPLOAD CSV PATHS
// ============================================================================

const getUploadCsvPath = (orgId, projectId, userId) => {
    if (!orgId || !projectId || !userId) return '';
    return buildPath(`${orgId}/uploadCsvLog/${projectId}/${userId}`);
};

const getUploadCsvLogPath = (orgId, projectId, userId, ticketId) => {
    if (!orgId || !projectId || !userId || !ticketId) return '';
    return `${getUploadCsvPath(orgId, projectId, userId)}/${ticketId}.log`;
};

const getUploadCsvTempLogPath = (orgId, projectId, userId, uploadKey, currentLine) => {
    if (!orgId || !projectId || !userId || !uploadKey || currentLine === undefined) return '';
    return `${getUploadCsvPath(orgId, projectId, userId)}/${uploadKey}/${currentLine}.log`;
};

// ============================================================================
// ASSETS PATHS
// ============================================================================

const getAssetsPath = (orgId, projectId) => {
    if (!orgId || !projectId) return '';
    return buildPath(`assets/${orgId}/${projectId}`);
};

const getCssAssetPath = (orgId, projectId, cssType, version) => {
    if (!orgId || !projectId || !cssType || !version) return '';
    const cssFileName = cssType === 'knowledge-robo' 
        ? `knowledge-robo-v${version}.css` 
        : `public-knowledge-v${version}.css`;
    return `${getAssetsPath(orgId, projectId)}/style/${cssFileName}`;
};

const getChatGptModelPath = () => {
    return buildPath('assets/listChatGptModel');
};

// ============================================================================
// SDAUTO PATHS
// ============================================================================

const getSdautoPath = (orgId, projectId, chatThreadId) => {
    if (!orgId || !projectId || !chatThreadId) return '';
    return buildPath(`${orgId}/${projectId}/sdauto/${chatThreadId}`);
};

const getSdautoDataPath = (orgId, projectId, chatThreadId, chatPostId) => {
    if (!orgId || !projectId || !chatThreadId || !chatPostId) return '';
    return `${getSdautoPath(orgId, projectId, chatThreadId)}/data/${chatPostId}`;
};

const getSdautoParameterPath = (chatThreadId) => {
    if (!chatThreadId) return '';
    return buildPath(`tmp/sdauto/parameter/${chatThreadId}`);
};

// ============================================================================
// AI CHAT PATHS
// ============================================================================

const getAiChatLogPath = (aiChatThreadId) => {
    if (!aiChatThreadId) return '';
    return buildPath(`log-chat-ai/${aiChatThreadId}.json`);
};

const getAiChatUploadsTmpPath = (orgId, projectId, chatThreadId) => {
    if (!orgId || !projectId || !chatThreadId) return '';
    return `tmp/${orgId}/${projectId}/${chatThreadId}`;
};

// ============================================================================
// TEMP PATHS
// ============================================================================

const getTempPath = (orgId, timestamp) => {
    if (!orgId || !timestamp) return '';
    return buildPath(`tmp/${orgId}/${timestamp}`);
};

const isTmpPath = (path) => {
    if (!path) return false;
    return path.includes('public/tmp/') || path.startsWith('tmp/');
};

// ============================================================================
// TICKET FIELD CONTENT PATHS (S3-stored field values: Markdown, JSON)
// ============================================================================

/**
 * Permanent S3 path for a Markdown field value on a ticket.
 * @param {string} ticketId
 * @param {string} fieldId
 * @param {string} orgId - affiliationOrganization
 * @returns {string} e.g. `public/assets/no-cache/markdown/<orgId>/ticket/<ticketId>/<fieldId>.md`
 */
const getTicketFieldMarkdownPath = (ticketId, fieldId, orgId) => {
    if (!ticketId || !fieldId || !orgId) return '';
    return `${S3_PREFIX}/assets/no-cache/markdown/${orgId}/ticket/${ticketId}/${fieldId}.md`;
};

/**
 * Permanent S3 path for a JSON field value on a ticket.
 * @param {string} ticketId
 * @param {string} fieldId
 * @param {string} orgId - affiliationOrganization
 * @returns {string} e.g. `public/assets/no-cache/json/<orgId>/ticket/<ticketId>/<fieldId>.json`
 */
const getTicketFieldJsonPath = (ticketId, fieldId, orgId) => {
    if (!ticketId || !fieldId || !orgId) return '';
    return `${S3_PREFIX}/assets/no-cache/json/${orgId}/ticket/${ticketId}/${fieldId}.json`;
};

/**
 * Temporary S3 path for a Markdown field (create-ticket flow).
 * The file is later copied to the permanent path via getTicketFieldMarkdownPath.
 * @param {string} fieldId
 * @returns {string} e.g. `public/tmp/markdown/<fieldId>_random_id_<uuid>.md`
 */
const getTmpMarkdownFieldPath = (fieldId) => {
    return `${S3_PREFIX}/tmp/markdown/${fieldId}_random_id_${uuidv4()}.md`;
};

/**
 * Temporary S3 path for a JSON field (create-ticket flow).
 * The file is later copied to the permanent path via getTicketFieldJsonPath.
 * @param {string} fieldId
 * @returns {string} e.g. `public/tmp/json/<fieldId>_random_id_<uuid>.json`
 */
const getTmpJsonFieldPath = (fieldId) => {
    return `${S3_PREFIX}/tmp/json/${fieldId}_random_id_${uuidv4()}.json`;
};

// ============================================================================
// HOLD MUSIC PATHS
// ============================================================================

/**
 * Temporary S3 path for hold music file upload.
 * Files in public/tmp/ are auto-deleted after 1 day by S3 Lifecycle rule.
 * Format: {fileNameWithoutExt}-{timestamp}.{ext}
 * @param {string} projectId
 * @param {string} fileName - original file name (e.g. "my-hold-music.mp3")
 * @returns {string} e.g. `public/tmp/project-audio/{projectId}/my-hold-music-1718100000000.mp3`
 */
const getTmpHoldMusicPath = (projectId, fileName) => {
    if (!projectId || !fileName) return '';
    const lastDotIndex = fileName.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
    const ext = lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1).toLowerCase() : '';
    const timestamp = Date.now();
    return buildPath(`tmp/project-audio/${projectId}/${nameWithoutExt}-${timestamp}.${ext}`);
};

/**
 * Extract original file name from hold music S3 key.
 * Removes the timestamp suffix appended before the extension.
 * e.g. "public/project-audio/proj123/my-hold-music-1718100000000.mp3" → "my-hold-music.mp3"
 * @param {string} s3Key - full S3 key
 * @returns {string} original file name
 */
const getOriginalHoldMusicFileName = (s3Key) => {
    if (!s3Key) return '';
    const fullFileName = s3Key.split('/').pop();
    const lastDotIndex = fullFileName.lastIndexOf('.');
    const ext = lastDotIndex > 0 ? fullFileName.substring(lastDotIndex + 1) : '';
    const nameWithoutExt = lastDotIndex > 0 ? fullFileName.substring(0, lastDotIndex) : fullFileName;
    const lastDashIndex = nameWithoutExt.lastIndexOf('-');
    const originalName = lastDashIndex > 0 ? nameWithoutExt.substring(0, lastDashIndex) : nameWithoutExt;
    return ext ? `${originalName}.${ext}` : originalName;
};

// ============================================================================
// REPLACE FILE PATHS
// ============================================================================

/**
 * Temporary S3 path for replace file upload.
 * Files in public/tmp/ are auto-deleted after 1 day by S3 Lifecycle rule.
 * @param {string} orgId - corporation ID
 * @param {string} ticketId - ticket ID
 * @param {string} fileName - original file name (e.g. "report.pdf")
 * @returns {string} e.g. `tmp/{orgId}/{ticketId}/{timestamp}/{filename}` (without public/ prefix, for Storage.put)
 */
const getTmpReplaceFilePath = (orgId, ticketId, fileName) => {
    if (!orgId || !fileName || !ticketId) return '';
    const timestamp = Date.now();
    return `tmp/${orgId}/${ticketId}/${timestamp}/${fileName}`;
};

// ============================================================================
// KNOWLEDGE PATHS
// ============================================================================

const getKnowledgePath = (ticketId) => {
    if (!ticketId) return '';
    return `knowledge/${ticketId}`;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const addPublicPrefix = (path) => {
    if (!path) return '';
    if (path.startsWith(`${S3_PREFIX}/`) || path.startsWith(`/${S3_PREFIX}/`)) {
        return path;
    }
    return `${S3_PREFIX}/${path}`;
};

const removePublicPrefix = (path) => {
    if (!path) return '';
    if (path.startsWith(`${S3_PREFIX}/`)) {
        return path.substring(S3_PREFIX.length + 1);
    }
    if (path.startsWith(`/${S3_PREFIX}/`)) {
        return path.substring(S3_PREFIX.length + 2);
    }
    return path;
};

const hasPublicPrefix = (path) => {
    if (!path) return false;
    return path.startsWith(`${S3_PREFIX}/`) || path.startsWith(`/${S3_PREFIX}/`);
};

const getSnapshotAssistantTicketPath = (orgId, ticketId, aiAssistantChatThreadId) => {
    return `${orgId}/ticket/${ticketId}/ai-assistant-snapshot/${aiAssistantChatThreadId}/prompt-data.json`;
}

const S3PathUtils = {
    S3_PREFIX,
    buildPath,
    getTranslationPath,
    getTicketPath,
    getTicketVectorDataPath,
    getTicketChatThreadPath,
    getTelephoneTranscriptAudioPath,
    getCachePath,
    getSystemFieldCachePath,
    getSystemTagCachePath,
    getStatusItemsCachePath,
    getUploadCsvPath,
    getUploadCsvLogPath,
    getUploadCsvTempLogPath,
    getAssetsPath,
    getCssAssetPath,
    getChatGptModelPath,
    getSdautoPath,
    getSdautoDataPath,
    getSdautoParameterPath,
    getAiChatLogPath,
    getAiChatUploadsTmpPath,
    getTempPath,
    isTmpPath,
    getTmpHoldMusicPath,
    getOriginalHoldMusicFileName,
    getTmpReplaceFilePath,
    getKnowledgePath,
    addPublicPrefix,
    removePublicPrefix,
    hasPublicPrefix,
    getSnapshotAssistantTicketPath,
    getTicketFieldMarkdownPath,
    getTicketFieldJsonPath,
    getTmpMarkdownFieldPath,
    getTmpJsonFieldPath,
    defineTelephoneTranscriptPath,
    getPublicTelephoneTranscriptUrl,
    defineTelephoneRecordingAudioPath,
    getPublicTelephoneRecordingAudioUrl,
};

export default S3PathUtils;